import React from 'react'
import { Box, Typography, keyframes } from '@mui/material'
import { styled } from '@mui/material/styles'
import type { SpaceDefinition } from '../spaceCatalog'

export interface SpaceCardProps {
  space: SpaceDefinition
  index?: number
  onClick?: () => void
}

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`

const Root = styled(Box)({
  position: 'relative',
  width: '100%',
  borderRadius: 'var(--ds-radius-lg)',
  overflow: 'hidden',
  cursor: 'pointer',
  backgroundColor: 'var(--ds-canvas)',
  boxShadow: 'var(--ds-shadow-soft)',
  animation: `${fadeUp} 480ms var(--ds-ease) both`,
  transition: [
    'transform var(--ds-duration-normal) var(--ds-ease-out, var(--ds-ease))',
    'box-shadow var(--ds-duration-normal) var(--ds-ease)',
  ].join(', '),
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: 'var(--ds-shadow-lift)',
  },
  '&:hover .space-card-edge': { opacity: 1 },
  '&:hover .space-card-img': { transform: 'scale(1.05)' },
})

const SpectrumEdge = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: 2.5,
  zIndex: 3,
  background: 'var(--ds-gradient-spectrum, var(--ds-ink))',
  opacity: 0,
  transition: 'opacity var(--ds-duration-normal) var(--ds-ease)',
})

const Cov = styled(Box)({
  position: 'relative',
  aspectRatio: '4 / 3.05',
  overflow: 'hidden',
  backgroundColor: 'var(--ds-night-canvas)',
})

const CoverImg = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
  filter: 'saturate(0.92) contrast(1.04)',
  transition: 'transform 700ms cubic-bezier(0.16,1,0.3,1)',
})

const Veil = styled(Box)({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(180deg, transparent 45%, rgba(13,13,15,0.72) 100%)',
  pointerEvents: 'none',
  zIndex: 2,
})

const badgeColor: Record<SpaceDefinition['badgeKind'], string> = {
  hall: 'var(--ds-accent-dot-4, #01dafe)',
  living: 'var(--ds-accent-dot-1, #ffcf7d)',
  spaces: 'var(--ds-accent-dot-3, #be9dfd)',
  vision: 'var(--ds-accent-dot-2, #fea67d)',
}

export function SpaceCard({ space, index = 0, onClick }: SpaceCardProps) {
  return (
    <Root
      onClick={onClick}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
      sx={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
    >
      <SpectrumEdge className='space-card-edge' />
      <Cov>
        <CoverImg className='space-card-img' src={space.thumbUrl} alt={space.title} loading='lazy' />
        <Veil />
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            zIndex: 3,
            height: 24,
            display: 'inline-flex',
            alignItems: 'center',
            px: '10px',
            borderRadius: 'var(--ds-radius-pill)',
            background: 'rgba(13,13,15,0.65)',
            backdropFilter: 'blur(8px)',
            fontFamily: 'var(--ds-font-mono)',
            fontSize: 8.5,
            letterSpacing: '0.12em',
            color: badgeColor[space.badgeKind],
            fontWeight: 600,
          }}
        >
          {space.badge}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 3,
            height: 24,
            display: 'inline-flex',
            alignItems: 'center',
            px: '10px',
            borderRadius: 'var(--ds-radius-pill)',
            background: 'rgba(255,255,255,0.92)',
            fontFamily: 'var(--ds-font-mono)',
            fontSize: 8.5,
            letterSpacing: '0.1em',
            color: 'var(--ds-ink)',
            fontWeight: 600,
          }}
        >
          {space.statusLabel}
        </Box>
      </Cov>
      <Box sx={{ p: '14px 16px 16px' }}>
        <Typography sx={{ fontSize: 15, fontWeight: 680, letterSpacing: '-0.015em' }}>{space.title}</Typography>
        <Typography sx={{ fontSize: 12, color: 'var(--ds-ink-muted)', mt: '6px', lineHeight: 1.4 }}>
          {space.blurb}
        </Typography>
        <Box display='flex' alignItems='center' justifyContent='space-between' gap={1} mt='12px'>
          <Typography
            sx={{
              fontFamily: 'var(--ds-font-mono)',
              fontSize: 9.5,
              letterSpacing: '0.08em',
              color: 'var(--ds-ink-subtle)',
              textTransform: 'uppercase',
            }}
          >
            {space.layers}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 650, color: 'var(--ds-accent-deep-b, #6b4bb8)', whiteSpace: 'nowrap' }}>
            Enter →
          </Typography>
        </Box>
      </Box>
    </Root>
  )
}
